import { useTours } from 'features/Responder/features/Tours';
import TourMap from 'features/Responder/features/Tours/features/TourMap';
import {
  TourContextMenu,
  TourToolsPanel,
} from 'features/Responder/features/Tours/features/TourMap/Components';
import { fitMapToZone, useZones } from 'features/Responder/features/Zones';
import { buildEntitySelectionFocusKey } from 'features/Responder/utils/focusKeys';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MultiRouteLayer,
  ResetZoomControl,
  SelectedToursCheckpointsLayer,
  SelectedZoneFocus,
  ToolsContextMenu,
  ZoneEditLayer,
  ZoneLayer,
  ZonePlacementPreview,
} from './components';
import { useResponderMap } from './contexts/ResponderMapContext';
import { ZoneEditBar, ZoneToolsPanel } from './features';
import { useZonesActions } from './hooks';
import { ROUTE_COLORS, colorForIndex } from './utils/routeColors';

/**
 * Central responder map that layers zones and tours in one view.
 * tours still rely on TourContext; we proxy multi-select down
 * by setting the active tour to the first selected id for now.
 */
export default function ResponderMap({
  editable = true,
  enableTools = true,
  enableTourTools = true,
  enableContextMenu = true,
  enableZones = true,
  contextMenuMode = 'all',
  autoSelectZones = true,
  activeSelectionOnly = false,
  visibleZoneIds = null,
  enableZoneLayerFocusWhenEditing = false,
  showZoneEditorPanel = true,
  extraLayers = null,
  disableLocationSync = null,
  disableMapReset = true,
  showPersonnel = true,
  showGoogleLocationSearch = true,
  enableInitialUserLocation = true,
  fallbackMapCenter = [39.8283, -98.5795],
  initialMapZoom = 10,
  showResetZoomControl = true,
  resetZoomLevel = 4,
  focusOnPrimaryTourChange = false,
  focusOnPrimaryZoneChange = false,
}) {
  const {
    selectedTourIds,
    selectedZoneIds,
    setSelectedZoneIds,
    isolatedLayerDepth,
    setIsolatedLayerDepth,
    clearIsolation,
    showZoneTools,
    setShowZoneTools,
    showTourTools,
    setShowTourTools,
    showZoneEditor,
    setShowZoneEditor,
    showAllZones,
    setShowAllZones,
    showZoneAnchors,
    setShowZoneAnchors,
    zonePlacementPreview,
    setZonePlacementPreview,
  } = useResponderMap();
  const { tours, activeTourData, setMapTool, mapTool } = useTours();
  const {
    zones,
    zonesWithMeta,
    setActiveZoneId,
    setSelectedZoneIds: setZoneSelectedZoneIds,
    zonesEditing,
    setZonesEditing,
    activeZoneId,
    zoneEditMode,
    pushGeometryHistory,
  } = useZones();
  const hasInitializedZones = useRef(false);
  const { addZoneAt, updateZoneGeometry } = useZonesActions();
  const zonesEnabled = enableZones !== false;
  const toolsEnabled = enableTools !== false;
  const tourToolsEnabled = enableTourTools !== false;
  const contextMenuEnabled = enableContextMenu !== false;
  const resolvedContextMenuMode = useMemo(() => {
    if (!contextMenuEnabled) return 'none';
    const mode = (contextMenuMode || 'all').toLowerCase();
    if (mode === 'none') return 'none';
    if (mode === 'tour') return 'tour';
    if (!zonesEnabled) return 'tour';
    return 'all';
  }, [contextMenuEnabled, contextMenuMode, zonesEnabled]);
  const showToolsContextMenu = toolsEnabled && resolvedContextMenuMode === 'all';
  const showTourContextMenu = tourToolsEnabled && resolvedContextMenuMode === 'tour';
  const effectiveShowTourTools = toolsEnabled && tourToolsEnabled && showTourTools;
  const effectiveShowZoneTools =
    zonesEnabled &&
    toolsEnabled &&
    editable &&
    showZoneTools &&
    (zonesEditing || mapTool?.mode === 'zone');
  const effectiveShowZoneEditor =
    zonesEnabled && toolsEnabled && showZoneEditor && showZoneEditorPanel;
  const isAwaitingZoneShape =
    mapTool?.mode === 'zone' && !!mapTool?.awaitingShape && !mapTool?.shape;
  const isPlacingZone = mapTool?.mode === 'zone' && !!mapTool?.shape;
  const isZonePlacementFlow = mapTool?.mode === 'zone';
  const [zoneContextMenu, setZoneContextMenu] = useState(null);
  const zoneContextMenuSuppressUntilRef = useRef(0);
  const zoneFitSuppressUntilRef = useRef(0);

  useEffect(() => {
    if (!tourToolsEnabled && showTourTools) {
      setShowTourTools(false);
    }
  }, [setShowTourTools, showTourTools, tourToolsEnabled]);

  useEffect(() => {
    if (!isZonePlacementFlow) return undefined;
    const handleEscapeCancel = (event) => {
      if (event.key !== 'Escape') return;
      setMapTool?.(null);
      setZonePlacementPreview?.(null);
    };
    window.addEventListener('keydown', handleEscapeCancel);
    return () => {
      window.removeEventListener('keydown', handleEscapeCancel);
    };
  }, [isZonePlacementFlow, setMapTool, setZonePlacementPreview]);

  useEffect(() => {
    if (!zoneContextMenu) return undefined;
    const handlePointerDown = (event) => {
      if (event?.target?.closest?.('.zone-read-context-menu')) return;
      setZoneContextMenu(null);
    };
    const handleContextMenu = (event) => {
      if (event?.target?.closest?.('.zone-read-context-menu')) return;
      setZoneContextMenu(null);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setZoneContextMenu(null);
      }
    };
    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [zoneContextMenu]);

  useEffect(() => {
    if (zonesEditing) {
      setZoneContextMenu(null);
    }
  }, [zonesEditing]);

  // Default zones: show all on first load when none are selected.
  useEffect(() => {
    if (!autoSelectZones || !zonesEnabled || activeSelectionOnly) return;
    if (hasInitializedZones.current) return;
    if (selectedZoneIds.length || !Array.isArray(zones) || !zones.length) return;
    const allIds = zones.map((z) => z.id).filter((id) => id != null);
    if (allIds.length) {
      setSelectedZoneIds(allIds);
      hasInitializedZones.current = true;
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectZones, selectedZoneIds.length, setSelectedZoneIds, zones]);

  /**
   * Focus and isolate the chosen zone on the map.
   * @param {object} zone
   * @param {object} map
   */
  const handleSelectZone = useCallback(
    (zone, map) => {
      if (!zone?.id) return;
      if (showAllZones) {
        setIsolatedLayerDepth(null);
      } else {
        const depth = Number(zone.layer);
        if (Number.isFinite(depth)) {
          setIsolatedLayerDepth((prev) => (prev === depth ? null : depth));
        }
      }
      const shouldSuppressFit =
        Date.now() < zoneFitSuppressUntilRef.current || zonesEditing || mapTool?.mode === 'zone';
      if (shouldSuppressFit) return;
      fitMapToZone(map, zone, zonesWithMeta);
    },
    [mapTool?.mode, setIsolatedLayerDepth, showAllZones, zonesEditing, zonesWithMeta],
  );

  const applyFocusedZoneSelection = useCallback(
    (zone) => {
      if (!zone?.id) return;
      const childIds = Array.isArray(zone?.childrenIds) ? zone.childrenIds : [];
      const nextSelection = Array.from(
        new Set([zone.id, ...childIds].filter((id) => id != null).map((id) => `${id}`)),
      );
      setSelectedZoneIds?.(nextSelection);
      setZoneSelectedZoneIds?.(nextSelection);
    },
    [setSelectedZoneIds, setZoneSelectedZoneIds],
  );

  const handleContextZone = useCallback(
    (zone, map, event) => {
      if (!zone?.id || zonesEditing) return;
      zoneContextMenuSuppressUntilRef.current = Date.now() + 350;
      const latlng = event?.latlng;
      const point = map?.latLngToContainerPoint?.(latlng);
      if (!point) return;
      setZoneContextMenu({
        zone,
        map,
        point: { x: point.x, y: point.y },
      });
    },
    [zonesEditing],
  );

  const handleEditZoneFromContext = useCallback(() => {
    const zone = zoneContextMenu?.zone;
    if (!zone?.id) return;
    setActiveZoneId?.(zone.id);
    applyFocusedZoneSelection(zone);
    handleSelectZone(zone, zoneContextMenu?.map);
    setZonesEditing?.(true);
    setZoneContextMenu(null);
  }, [
    applyFocusedZoneSelection,
    handleSelectZone,
    setActiveZoneId,
    setZonesEditing,
    zoneContextMenu,
  ]);

  const hasExplicitZoneSelection = selectedZoneIds.length > 0;
  const hasExplicitTourSelection = selectedTourIds.length > 0;
  const hasTourSelection = hasExplicitTourSelection || activeTourData?.id != null;
  const preferZones = activeSelectionOnly && hasExplicitZoneSelection && !hasTourSelection;

  const effectiveSelectedTourIds = useMemo(() => {
    if (!activeSelectionOnly) return selectedTourIds;
    if (preferZones) return [];
    if (hasExplicitTourSelection) return [selectedTourIds[0]];
    if (activeTourData?.id != null) return [activeTourData.id];
    return [];
  }, [
    activeTourData?.id,
    activeSelectionOnly,
    hasExplicitTourSelection,
    preferZones,
    selectedTourIds,
  ]);

  const effectiveSelectedZoneIds = useMemo(() => {
    if (!activeSelectionOnly) return selectedZoneIds;
    return preferZones ? selectedZoneIds : [];
  }, [activeSelectionOnly, preferZones, selectedZoneIds]);

  const effectiveVisibleZoneIds = useMemo(() => {
    if (Array.isArray(visibleZoneIds)) return visibleZoneIds.map((id) => `${id}`);
    return effectiveSelectedZoneIds;
  }, [effectiveSelectedZoneIds, visibleZoneIds]);

  const colorMap = useMemo(() => {
    const map = new Map();
    effectiveSelectedTourIds.forEach((id, idx) => {
      map.set(`${id}`, ROUTE_COLORS[idx % ROUTE_COLORS.length]);
    });
    return map;
  }, [effectiveSelectedTourIds]);

  const primarySelectedTour = useMemo(() => {
    if (!effectiveSelectedTourIds.length) return null;
    const firstId = effectiveSelectedTourIds[0];
    return tours.find((gt) => `${gt.id}` === `${firstId}`) || null;
  }, [effectiveSelectedTourIds, tours]);

  const primarySelectedZone = useMemo(() => {
    if (!effectiveSelectedZoneIds.length) return null;
    const firstId = effectiveSelectedZoneIds[0];
    const zoneList =
      Array.isArray(zonesWithMeta) && zonesWithMeta.length ? zonesWithMeta : zones || [];
    return zoneList.find((zone) => `${zone?.id}` === `${firstId}`) || null;
  }, [effectiveSelectedZoneIds, zones, zonesWithMeta]);

  const zoneFocusKey = useMemo(() => {
    return buildEntitySelectionFocusKey({
      enabled: focusOnPrimaryZoneChange,
      primaryId: primarySelectedZone?.id,
      selectedIds: effectiveSelectedZoneIds,
    });
  }, [effectiveSelectedZoneIds, focusOnPrimaryZoneChange, primarySelectedZone?.id]);

  const overlays = useMemo(
    () => (
      <>
        {focusOnPrimaryZoneChange && primarySelectedZone && !effectiveSelectedTourIds.length && (
          <SelectedZoneFocus
            zone={primarySelectedZone}
            zones={zonesWithMeta}
            focusKey={zoneFocusKey}
          />
        )}
        {zonesEnabled && (
          <ZoneLayer
            zones={zonesWithMeta}
            selectedZoneIds={effectiveVisibleZoneIds}
            isolatedLayerDepth={isolatedLayerDepth}
            interactive={!isPlacingZone && (!zonesEditing || enableZoneLayerFocusWhenEditing)}
            onFocusZone={(zone, map) => {
              if (!zone?.id) return;
              setActiveZoneId?.(zone.id);
              applyFocusedZoneSelection(zone);
              handleSelectZone(zone, map);
            }}
            onContextZone={handleContextZone}
          />
        )}
        {zonesEnabled && toolsEnabled && (
          <ZonePlacementPreview
            latlng={zonePlacementPreview}
            shape={mapTool?.shape || 'square'}
            sizeMeters={mapTool?.sizeMeters}
          />
        )}
        <MultiRouteLayer
          tours={tours}
          selectedTourIds={effectiveSelectedTourIds}
          activeTourId={activeTourData?.id}
          colorMap={colorMap}
        />
        {zonesEnabled && toolsEnabled && zonesEditing && !isPlacingZone && (
          <ZoneEditLayer
            zones={zonesWithMeta}
            selectedZoneIds={effectiveSelectedZoneIds}
            activeZoneId={activeZoneId}
            zoneEditMode={zoneEditMode}
            onFocusZone={(zone) => {
              if (!zone?.id) return;
              setActiveZoneId?.(zone.id);
              applyFocusedZoneSelection(zone);
            }}
            onUpdateZone={(id, pts) => {
              if (!id || !Array.isArray(pts)) return;
              setActiveZoneId?.(id);
              const nextSelection = [`${id}`];
              setSelectedZoneIds?.(nextSelection);
              setZoneSelectedZoneIds?.(nextSelection);
              updateZoneGeometry?.(id, pts);
            }}
            onBeginEdit={(id, priorPoints) => {
              if (!id || !Array.isArray(priorPoints)) return;
              const snapshot = priorPoints.map((p) => ({ ...p }));
              pushGeometryHistory?.(id, snapshot);
            }}
            editing={zonesEditing}
          />
        )}
        <SelectedToursCheckpointsLayer
          tours={tours}
          selectedTourIds={effectiveSelectedTourIds}
          activeTourId={activeTourData?.id}
          colorMap={colorMap}
        />
        {showToolsContextMenu && (
          <ToolsContextMenu
            onAddAt={(latlng, shape) => addZoneAt(latlng, shape)}
            suppressOnTargets={['.responder-zone-shape']}
            suppressContextMenuRef={zoneContextMenuSuppressUntilRef}
            allowTourTools={tourToolsEnabled}
            allowZoneEditorToggle={showZoneEditorPanel}
            showZoneTools={showZoneTools}
            showTourTools={effectiveShowTourTools}
            showZoneEditor={effectiveShowZoneEditor}
            showAllZones={showAllZones}
            setShowZoneTools={setShowZoneTools}
            setShowTourTools={setShowTourTools}
            setShowZoneEditor={setShowZoneEditor}
            setShowAllZones={setShowAllZones}
            setMapTool={setMapTool}
          />
        )}
        {showTourContextMenu && (
          <TourContextMenu
            showTourTools={effectiveShowTourTools}
            setShowTourTools={setShowTourTools}
            showZoneAnchors={showZoneAnchors}
            setShowZoneAnchors={setShowZoneAnchors}
          />
        )}
        {showResetZoomControl && <ResetZoomControl zoomLevel={resetZoomLevel} />}
        {extraLayers}
      </>
    ),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeTourData?.id,
      tours,
      handleSelectZone,
      handleContextZone,
      isolatedLayerDepth,
      colorMap,
      zoneFocusKey,
      primarySelectedZone,
      applyFocusedZoneSelection,
      zonePlacementPreview,
      effectiveSelectedTourIds,
      effectiveSelectedZoneIds,
      effectiveVisibleZoneIds,
      focusOnPrimaryZoneChange,
      enableZoneLayerFocusWhenEditing,
      zonesEnabled,
      toolsEnabled,
      tourToolsEnabled,
      showToolsContextMenu,
      showTourContextMenu,
      mapTool?.shape,
      isPlacingZone,
      effectiveShowZoneTools,
      showAllZones,
      setShowZoneTools,
      setShowTourTools,
      selectedTourIds,
      selectedZoneIds,
      zones,
      zonesWithMeta,
      effectiveShowTourTools,
      effectiveShowZoneEditor,
      setShowZoneEditor,
      setShowAllZones,
      showZoneAnchors,
      setShowZoneAnchors,
      showResetZoomControl,
      resetZoomLevel,
      extraLayers,
      zoneEditMode,
    ],
  );

  const isolatedLayerBadge =
    isolatedLayerDepth != null ? (
      <div
        className="position-absolute"
        style={{
          top: 16,
          left: 16,
          zIndex: 1,
          background: 'rgba(10, 20, 35, 0.8)',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: 8,
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
        }}
      >
        <div className="fw-semibold">Isolated Layer</div>
        <div className="small text-muted">Layer {isolatedLayerDepth}</div>
        <button className="btn btn-sm btn-outline-light mt-2" onClick={clearIsolation}>
          Show all layers
        </button>
      </div>
    ) : null;

  const toolsOverlay = toolsEnabled ? (
    <div
      className={`position-absolute top-0 end-0 h-100 d-flex flex-column gap-2 p-2 overflow-auto ${
        isAwaitingZoneShape ? 'zone-shape-guided-tools' : ''
      }`}
      style={{ zIndex: 4, pointerEvents: 'none' }}
    >
      {effectiveShowTourTools && (
        <div style={{ pointerEvents: 'auto' }}>
          <TourToolsPanel
            mapTool={mapTool}
            setMapTool={setMapTool}
            showTourTools={effectiveShowTourTools}
            setShowTourTools={setShowTourTools}
            stacked
          />
        </div>
      )}
      {effectiveShowZoneTools && (
        <div style={{ pointerEvents: 'auto' }}>
          <ZoneToolsPanel />
        </div>
      )}
      {effectiveShowZoneEditor && (
        <div style={{ pointerEvents: 'auto' }}>
          <ZoneEditBar />
        </div>
      )}
    </div>
  ) : null;

  // Share TourMap's isolated context so badge (1) < guided overlays (2/3) < tools (4).
  const mapOverlay =
    isolatedLayerBadge || toolsOverlay ? (
      <>
        {isolatedLayerBadge}
        {toolsOverlay}
      </>
    ) : null;

  return (
    <div className="position-relative w-100 h-100" style={{ isolation: 'isolate' }}>
      <TourMap
        extraLayers={overlays}
        routeColor={colorMap.get(`${activeTourData?.id}`) || colorForIndex(0)}
        editable={editable}
        viewTour={primarySelectedTour}
        focusViewTourOnChange={focusOnPrimaryTourChange}
        addZoneAt={zonesEnabled ? addZoneAt : null}
        onZonePlaced={
          zonesEnabled
            ? () => {
                zoneFitSuppressUntilRef.current = Date.now() + 900;
                setZonesEditing(true);
              }
            : null
        }
        setZonePlacementPreview={zonesEnabled ? setZonePlacementPreview : null}
        maxZoom={23}
        showTourTools={effectiveShowTourTools}
        setShowTourTools={setShowTourTools}
        showZoneAnchors={showZoneAnchors}
        showTourToolsPanel={false}
        toolsOverlay={mapOverlay}
        disableLocationSync={disableLocationSync}
        disableMapReset={disableMapReset}
        showPersonnel={showPersonnel}
        showGoogleLocationSearch={showGoogleLocationSearch}
        enableInitialUserLocation={enableInitialUserLocation}
        fallbackMapCenter={fallbackMapCenter}
        initialMapZoom={initialMapZoom}
      />
      {!zonesEditing && zoneContextMenu?.zone && (
        <div
          className="zone-read-context-menu responder-map-context-menu card border border-theme-strong shadow-lg bg-primary-shadow text-theme"
          style={{
            position: 'absolute',
            left: zoneContextMenu.point.x,
            top: zoneContextMenu.point.y,
            zIndex: 5,
            minWidth: 170,
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="px-3 pt-2 pb-1 text-uppercase small text-theme-muted">Zone Actions</div>
          <div className="px-3 pb-2 fw-semibold">
            {zoneContextMenu.zone?.name || `Zone #${zoneContextMenu.zone?.id ?? ''}`}
          </div>
          <button
            type="button"
            className="btn btn-sm text-start w-100 text-decoration-none px-3 py-2 responder-map-context-link responder-interactive-action"
            onClick={handleEditZoneFromContext}
          >
            Open in Zone Editor
          </button>
        </div>
      )}
    </div>
  );
}

export { ResponderMapProvider, useResponderMap } from './contexts/ResponderMapContext';
export {
  MapLayersPanel,
  TourSelectorPanel,
  ZoneControlsPanel,
  ZoneEditBar,
  ZoneToolsPanel,
  ZoneTreePanel,
} from './features';
export { ScheduleMapView, TourMapEdit, TourMapView, ZoneMapEdit, ZoneMapView } from './wrappers';
